import React, { useEffect, useState } from 'react';
import { admin } from '../lib/api';
import { Users, FileText, Shield, Activity } from 'lucide-react';
import { format } from 'date-fns';

const ACTION_STYLES = {
  upload: 'bg-blue-50 text-blue-700',
  publish: 'bg-green-50 text-green-700',
  login: 'bg-gray-100 text-gray-600',
  delete: 'bg-red-50 text-red-600',
};

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [actionModal, setActionModal] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    admin.stats().then(r => setStats(r.data));
    admin.users().then(r => setUsers(r.data));
    admin.audit().then(r => setAudit(r.data));
    admin.demoRequests().then(r => setDemoRequests(r.data));
  }, []);

  const fmtSize = (bytes) => {
    if (!bytes) return '0 B';
    const gb = bytes / 1e9;
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    return `${(bytes / 1e6).toFixed(1)} MB`;
  };

  const handleDemoAction = async (id, status) => {
    setActionLoading(true);
    try {
      const res = await admin.updateDemoRequest(id, { status, admin_notes: adminNotes });
      setDemoRequests(prev => prev.map(r => r.id === id ? res.data : r));
      setActionModal(null);
      setAdminNotes('');
    } catch (e) {
      alert('Failed to update request');
    }
    setActionLoading(false);
  };

  const ROLE_STYLES = { admin: 'bg-purple-50 text-purple-700', staff: 'bg-blue-50 text-blue-700', public: 'bg-gray-100 text-gray-600' };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Shield size={20} className="text-purple-500" />
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Admin console</h1>
          <p className="text-sm text-gray-500 mt-0.5">System overview, users, and audit trail</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {[['overview','Overview'],['users','Users'],['audit','Audit log'],['demos','Demo requests']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${tab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && stats && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total records', value: stats.total_records?.toLocaleString(), sub: fmtSize(stats.total_size_bytes), icon: FileText, color: 'bg-blue-50 text-blue-600' },
              { label: 'Published public', value: stats.published_records?.toLocaleString(), sub: 'On public portal', icon: Activity, color: 'bg-green-50 text-green-600' },
              { label: 'Staff users', value: stats.total_users, sub: 'Active accounts', icon: Users, color: 'bg-purple-50 text-purple-600' },
              { label: 'Migrations done', value: stats.completed_migrations, sub: 'Alchemy imports', icon: Shield, color: 'bg-amber-50 text-amber-600' },
            ].map(({ label, value, sub, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-100 p-5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${color}`}>
                  <Icon size={16} />
                </div>
                <div className="text-2xl font-semibold text-gray-900">{value ?? '—'}</div>
                <div className="text-sm text-gray-500 mt-0.5">{label}</div>
                <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          {stats.by_type?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h2 className="text-sm font-medium text-gray-700 mb-4">Records by document type</h2>
              <div className="space-y-3">
                {stats.by_type.map(({ document_type, count }) => {
                  const pct = stats.total_records > 0 ? Math.round((count / stats.total_records) * 100) : 0;
                  return (
                    <div key={document_type} className="flex items-center gap-3">
                      <div className="text-sm text-gray-600 w-36 truncate">{document_type}</div>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-teal-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-sm text-gray-500 w-12 text-right">{parseInt(count).toLocaleString()}</div>
                      <div className="text-xs text-gray-400 w-8 text-right">{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'users' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-gray-50 bg-gray-50/50">
              {['Name','Email','Role','Department','Joined'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold flex-shrink-0">
                        {u.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_STYLES[u.role] || 'bg-gray-100 text-gray-600'}`}>{u.role}</span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">{u.department || '—'}</td>
                  <td className="px-5 py-3.5 text-xs text-gray-400">{u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'audit' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-gray-50 bg-gray-50/50">
              {['Time','User','Action','Record','Details'].map(h => (
                <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {audit.map(a => {
                const d = a.details || {};
                const detail = a.action === 'login' ? `Logged in as ${d.email || '—'}`
                  : a.action === 'upload' ? `Uploaded "${d.title || d.file_name || '—'}"`
                  : a.action === 'search' ? `Searched "${d.query || '—'}" → ${d.results_count ?? '?'} results`
                  : a.action === 'publish' ? `${d.title || '—'} → published to public portal`
                  : a.action === 'unpublish' ? `${d.title || '—'} → removed from public portal`
                  : a.action === 'redaction_analyze' ? `Analyzed "${d.title || '—'}" — ${d.entity_count ?? d.entities_found ?? '?'} entities`
                  : a.action === 'redaction_apply' ? `Applied redactions → created redacted copy`
                  : d.title || d.query || '—';
                return (
                  <tr key={a.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40">
                    <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">{format(new Date(a.created_at), 'MMM d, HH:mm:ss')}</td>
                    <td className="px-5 py-3 text-sm text-gray-700">{a.user_name || 'System'}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ACTION_STYLES[a.action] || 'bg-gray-100 text-gray-600'}`}>{a.action}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-400 font-mono">{a.record_id ? a.record_id.slice(0, 8) + '...' : '—'}</td>
                    <td className="px-5 py-3 text-xs text-gray-500 max-w-xs truncate">{detail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {tab === 'demos' && (
        <div className="space-y-4">
          {demoRequests.filter(r => r.status === 'pending').length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
              {demoRequests.filter(r => r.status === 'pending').length} pending request(s) awaiting review
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-gray-50 bg-gray-50/50">
                {['Name','Organization','Email','Message','Received','Status','Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {demoRequests.map(r => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40">
                    <td className="px-5 py-3.5 text-sm font-medium text-gray-900">{r.name}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-500">{r.org || '—'}</td>
                    <td className="px-5 py-3.5 text-sm text-blue-600">
                      <a href={`mailto:${r.email}`}>{r.email}</a>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-500 max-w-[200px] truncate">{r.message || '—'}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                      {format(new Date(r.created_at), 'MMM d, yyyy HH:mm')}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        r.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                        r.status === 'approved' ? 'bg-green-50 text-green-700' :
                        r.status === 'scheduled' ? 'bg-blue-50 text-blue-700' :
                        'bg-red-50 text-red-600'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      {r.status === 'pending' ? (
                        <div className="flex gap-2">
                          <button onClick={() => { setActionModal({ request: r, action: 'approved' }); setAdminNotes(''); }}
                            className="text-xs px-2.5 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 font-medium">
                            Grant Access
                          </button>
                          <button onClick={() => { setActionModal({ request: r, action: 'scheduled' }); setAdminNotes(''); }}
                            className="text-xs px-2.5 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium">
                            Schedule
                          </button>
                          <button onClick={() => { setActionModal({ request: r, action: 'denied' }); setAdminNotes(''); }}
                            className="text-xs px-2.5 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100 font-medium">
                            Deny
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {r.access_code && <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{r.access_code}</span>}
                          {r.responded_by_name && <span className="ml-2">by {r.responded_by_name}</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {demoRequests.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-8 text-center text-sm text-gray-400">No demo requests yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Action Modal */}
          {actionModal && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setActionModal(null)}>
              <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {actionModal.action === 'approved' && 'Grant Demo Access'}
                  {actionModal.action === 'scheduled' && 'Schedule Demo'}
                  {actionModal.action === 'denied' && 'Deny Request'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  For <strong>{actionModal.request.name}</strong> ({actionModal.request.email})
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {actionModal.action === 'approved' ? 'Note (optional — included in email)' :
                     actionModal.action === 'scheduled' ? 'Meeting details (date, time, link — included in email)' :
                     'Reason (optional — included in email)'}
                  </label>
                  <textarea
                    rows={3}
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder={actionModal.action === 'scheduled' ? 'e.g. Wednesday April 10 at 2:00 PM ET — Zoom link: ...' : ''}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setActionModal(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDemoAction(actionModal.request.id, actionModal.action)}
                    disabled={actionLoading}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                      actionModal.action === 'approved' ? 'bg-green-600 hover:bg-green-700' :
                      actionModal.action === 'scheduled' ? 'bg-blue-600 hover:bg-blue-700' :
                      'bg-red-600 hover:bg-red-700'
                    } ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {actionLoading ? 'Sending...' :
                     actionModal.action === 'approved' ? 'Grant Access & Send Email' :
                     actionModal.action === 'scheduled' ? 'Schedule & Send Email' :
                     'Deny & Send Email'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
